import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { POSTS_GOAL_WEEKLY, POSTS_GOAL_MONTHLY } from '@/utils/analyticsConstants';

const ARC_START_ANGLE = 135;
const ARC_END_ANGLE = 405;
const ARC_RANGE = ARC_END_ANGLE - ARC_START_ANGLE;

const SVG_WIDTH = 240;
const SVG_HEIGHT = 175;
const CX = 120;
const CY = 120;
const RADIUS = 85;
const NEEDLE_LENGTH = 65;
const COUNTERWEIGHT_LENGTH = 14;
const ARROW_HEAD_LENGTH = 10;
const ARROW_HEAD_HALF_WIDTH = 4;

interface PostSpeedometerProps {
  postsCount: number;
  timeRange: 'week' | 'month';
}

const polarToXY = (cx: number, cy: number, angle: number, r: number) => {
  const rad = (angle * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const describeArc = (startAngle: number, endAngle: number, r: number) => {
  const start = polarToXY(CX, CY, startAngle, r);
  const end = polarToXY(CX, CY, endAngle, r);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

const PostSpeedometer = ({ postsCount, timeRange }: PostSpeedometerProps) => {
  const goal = timeRange === 'week' ? POSTS_GOAL_WEEKLY : POSTS_GOAL_MONTHLY;
  const maxScale = goal * 2;
  const clampedValue = Math.min(maxScale, Math.max(0, postsCount));
  const goalExceeded = postsCount >= goal;
  const ratio = clampedValue / maxScale;

  const zones = useMemo(() => {
    const gf = goal / maxScale;
    return [
      { endFraction: gf * 0.5, color: '#ef4444' },
      { endFraction: gf * 0.8, color: '#f97316' },
      { endFraction: gf, color: '#eab308' },
      { endFraction: gf * 1.3, color: '#22c55e' },
      { endFraction: 1, color: '#10b981' },
    ];
  }, [goal, maxScale]);

  const currentColor = useMemo(() => {
    for (const zone of zones) {
      if (ratio <= zone.endFraction) return zone.color;
    }
    return zones[zones.length - 1].color;
  }, [ratio, zones]);

  const needleAngleDeg = ARC_START_ANGLE + ratio * ARC_RANGE;
  const needleStartRotation = ARC_START_ANGLE + 90;
  const needleEndRotation = needleAngleDeg + 90;
  const goalAngle = ARC_START_ANGLE + (goal / maxScale) * ARC_RANGE;
  const goalOuter = polarToXY(CX, CY, goalAngle, RADIUS + 8);
  const goalInner = polarToXY(CX, CY, goalAngle, RADIUS - 12);

  const tickValues = useMemo(() => {
    const ticks: number[] = [];
    const step = Math.max(1, Math.ceil(maxScale / 10));
    for (let v = 0; v <= maxScale; v += step) ticks.push(v);
    if (!ticks.includes(goal)) ticks.push(goal);
    return ticks.sort((a, b) => a - b);
  }, [goal, maxScale]);

  return (
    <div className="flex flex-col items-center justify-center h-full w-full">
      <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} className="w-full max-w-[240px]">
        {/* Background arc zones */}
        {zones.map((zone, i) => {
          const prevEnd = i === 0 ? 0 : zones[i - 1].endFraction;
          const sA = ARC_START_ANGLE + prevEnd * ARC_RANGE;
          const eA = ARC_START_ANGLE + zone.endFraction * ARC_RANGE;
          return (
            <path key={i} d={describeArc(sA, eA, RADIUS)} fill="none" stroke={zone.color} strokeWidth={10} strokeLinecap="round" opacity={0.15} />
          );
        })}

        {/* Active arc */}
        <motion.path
          key={`arc-${timeRange}`}
          d={describeArc(ARC_START_ANGLE, needleAngleDeg, RADIUS)}
          fill="none"
          stroke={currentColor}
          strokeWidth={10}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />

        {/* Goal marker */}
        <line
          x1={goalInner.x} y1={goalInner.y}
          x2={goalOuter.x} y2={goalOuter.y}
          stroke="currentColor" strokeWidth={2.5}
          className="text-foreground/50" strokeDasharray="3 2"
        />

        {/* Tick marks */}
        {tickValues.map(value => {
          const angle = ARC_START_ANGLE + (value / maxScale) * ARC_RANGE;
          const outer = polarToXY(CX, CY, angle, RADIUS + 4);
          const inner = polarToXY(CX, CY, angle, RADIUS - 8);
          const label = polarToXY(CX, CY, angle, RADIUS - 22);
          const isGoal = value === goal;
          return (
            <g key={value}>
              {!isGoal && (
                <line x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="currentColor" strokeWidth={1} className="text-muted-foreground/30" />
              )}
              <text x={label.x} y={label.y} textAnchor="middle" dominantBaseline="middle"
                className={isGoal ? "fill-foreground font-bold" : "fill-muted-foreground"}
                fontSize={isGoal ? 10 : 8}
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Needle group — all geometry drawn pointing UP (negative Y), rotated via SVG transform */}
        <g key={`needle-${timeRange}`} transform={`rotate(${needleEndRotation} ${CX} ${CY})`}>
          <animateTransform
            key={`needle-anim-${timeRange}`}
            attributeName="transform"
            type="rotate"
            from={`${needleStartRotation} ${CX} ${CY}`}
            to={`${needleEndRotation} ${CX} ${CY}`}
            dur="1.1s"
            fill="freeze"
            calcMode="spline"
            keyTimes="0;1"
            keySplines="0.22 1 0.36 1"
          />
          {/* Counterweight (below center) */}
          <line
            x1={CX} y1={CY}
            x2={CX} y2={CY + COUNTERWEIGHT_LENGTH}
            stroke={currentColor} strokeWidth={5} strokeLinecap="round" opacity={0.25}
          />
          {/* Shaft (above center) */}
          <line
            x1={CX} y1={CY}
            x2={CX} y2={CY - NEEDLE_LENGTH}
            stroke={currentColor} strokeWidth={3} strokeLinecap="round"
          />
          {/* Arrowhead */}
          <polygon
            points={`
              ${CX},${CY - NEEDLE_LENGTH}
              ${CX - ARROW_HEAD_HALF_WIDTH},${CY - NEEDLE_LENGTH + ARROW_HEAD_LENGTH}
              ${CX + ARROW_HEAD_HALF_WIDTH},${CY - NEEDLE_LENGTH + ARROW_HEAD_LENGTH}
            `}
            fill={currentColor}
          />
        </g>

        {/* Center pivot */}
        <circle cx={CX} cy={CY} r={6} className="fill-background" stroke={currentColor} strokeWidth={2} />
        <circle cx={CX} cy={CY} r={2.5} fill={currentColor} />
      </svg>

      {/* Status */}
      <div className="flex flex-col items-center gap-0.5 mt-1">
        {goalExceeded ? (
          <span className="text-xs font-semibold text-emerald-600">Świetne tempo!</span>
        ) : (
          <span className="text-xs font-medium text-orange-500">Dodaj gazu, postuj częściej!</span>
        )}
        <span className="text-lg font-bold text-foreground">{postsCount}/{goal}</span>
        <span className="text-[10px] text-muted-foreground -mt-1">
          {timeRange === 'week' ? 'postów / 7 dni' : 'postów / 30 dni'}
        </span>
      </div>
    </div>
  );
};

export default PostSpeedometer;
